import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ApolloClient,
  NormalizedCacheObject,
  useLazyQuery,
  useMutation,
} from "@apollo/client";
import {
  getCartIdFromCookie,
  saveCartCookie,
  deleteCartCookie,
} from "helpers/cart";
import {
  AdjustCartItemVariantSubscriptionInput,
  CartFragment,
  CartDocument,
  CartQuery,
  CartQueryVariables,
  GetCartFromUserQuery,
  GetCartFromUserDocument,
  GetCartFromUserQueryVariables,
  AdjustCartItemVariantDocument,
  AdjustCartItemVariantMutation,
  AdjustCartItemVariantMutationVariables,
  AdjustCartItemVariantSubscriptionDocument,
  AdjustCartItemVariantSubscriptionMutation,
  AdjustCartItemVariantSubscriptionMutationVariables,
  // MergeCartsDocument,
  // MergeCartsMutation,
  // MergeCartsMutationVariables,
  V3ValidationErrorFragment,
} from "services/graphql/generated";
import { CartContextInterface } from "../cart";
import { booleanFilter } from "@pepdirect/v3/helpers/booleanFilter";

export function useCartProvider(
  client: ApolloClient<NormalizedCacheObject>,
  currentUserId: string | null
): {
  cartContextValue: CartContextInterface;
} {
  const [cart, setCartState] = useState<CartFragment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Create lazy mutation to prevent SSR calls
  const [adjustCartItemVariantMutation] = useMutation<
    AdjustCartItemVariantMutation,
    AdjustCartItemVariantMutationVariables
  >(AdjustCartItemVariantDocument, { client });
  const [adjustCartItemVariantSubscription] = useMutation<
    AdjustCartItemVariantSubscriptionMutation,
    AdjustCartItemVariantSubscriptionMutationVariables
  >(AdjustCartItemVariantSubscriptionDocument, { client });
  const [getCartFromUserQuery] = useLazyQuery<
    GetCartFromUserQuery,
    GetCartFromUserQueryVariables
  >(GetCartFromUserDocument, { client });
  const [getCartByIdQuery] = useLazyQuery<CartQuery, CartQueryVariables>(
    CartDocument,
    { client }
  );
  // const [mergeCartsMutation] = useMutation<
  //   MergeCartsMutation,
  //   MergeCartsMutationVariables
  // >(MergeCartsDocument, { client });

  /**
   * Sanitizes cart data by stripping "product/" prefix from variant slugs
   */
  const sanitizeCart = useCallback(
    (cartData: CartFragment | null): CartFragment | null => {
      if (!cartData) return null;

      return {
        ...cartData,
        variantItems: cartData.variantItems?.map((item) => {
          if (!item?.variant?.slug) return item;

          return {
            ...item,
            variant: {
              ...item.variant,
              slug: item.variant.slug.replace(/^product\//, ""),
            },
          };
        }),
      };
    },
    []
  );

  /**
   * Wrapper around setCartState that sanitizes the cart data
   */
  const setCart = useCallback(
    (cartData: CartFragment | null) => {
      setCartState(sanitizeCart(cartData));
    },
    [sanitizeCart]
  );

  /**
   * Returns the current Cart ID, either from the cart object or from the cookie.
   * @returns The cart ID or null if no cart ID is found.
   */
  const getCartId = () => {
    const cartIdFromCookie = getCartIdFromCookie();
    if (cart?.id) return cart.id;
    if (cartIdFromCookie) return cartIdFromCookie;
    return null;
  };

  const getCartFromUser = useCallback(async () => {
    try {
      const { data: userData } = await getCartFromUserQuery();
      setCart(userData?.currentUser?.cart || null);
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, [getCartFromUserQuery, setCart]);

  const getCartById = useCallback(
    async (cartId: string) => {
      try {
        const { data } = await getCartByIdQuery({ variables: { id: cartId } });
        setCart(data?.cart || null);
      } catch {
        setCart(null);
      } finally {
        setLoading(false);
      }
    },
    [getCartByIdQuery, setCart]
  );

  const refreshCart = useCallback(() => {
    // logged in user: get cart from current user
    if (currentUserId) {
      getCartFromUser();
    }
    // anonymous user: try to get cart by ID from cookie
    else {
      const cartIdFromCookie = getCartIdFromCookie();
      if (cartIdFromCookie) {
        getCartById(cartIdFromCookie);
      } else {
        setCart(null);
        setLoading(false);
      }
    }
  }, [currentUserId, getCartFromUser, getCartById, setCart]);

  /**
   * Adds an quantity of GTINs to the cart.
   * @param dto The input containing the GTIN, quantity, and subscription interval (if applicable)
   */
  const addToCart = async (input: AdjustCartItemVariantSubscriptionInput) => {
    if (!input.variantGtin)
      throw new Error("No variantGtin, could not add product.");
    const cartId = getCartId();
    // productId is the ProductUniqueId
    const { variantGtin, quantity, subscriptionIntervalInDays } = input;

    // Check if item already exists in cart and sum quantities
    const existingItem = cart?.variantItems?.find(
      (item) => item?.variant.gtin === variantGtin
    );
    const finalQuantity = existingItem
      ? existingItem.quantity + quantity
      : quantity;

    try {
      if (subscriptionIntervalInDays) {
        // Use subscription mutation if subscription data exists
        const { data } = await adjustCartItemVariantSubscription({
          variables: {
            cartId,
            variantGtin,
            quantity: finalQuantity,
            subscriptionIntervalInDays,
          },
        });
        setCart(data?.adjustCartItemVariantSubscription || null);
      } else {
        // Use regular mutation for non-subscription items
        const { data } = await adjustCartItemVariantMutation({
          variables: {
            cartId: cartId,
            input: {
              variantGtin,
              quantity: finalQuantity,
            },
          },
        });
        setCart(data?.adjustCartItemVariant || null);
      }
    } catch (e) {
      // logged by errorLink; rethrow so callers can show the failure instead
      // of a false "added to cart"
      console.error("Error adding item to cart:", e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates an item in the cart.
   * @param input The input containing the variantGtin, quantity, and subscription interval (if applicable)
   */
  const updateCart = async (input: AdjustCartItemVariantSubscriptionInput) => {
    if (!input.variantGtin) {
      throw new Error("No productId, could not update product.");
    }
    const cartId = getCartId();
    const { variantGtin, quantity, subscriptionIntervalInDays } = input;

    try {
      if (subscriptionIntervalInDays) {
        // Use subscription mutation if subscription data exists
        const { data } = await adjustCartItemVariantSubscription({
          variables: {
            cartId,
            variantGtin,
            quantity,
            subscriptionIntervalInDays,
          },
        });
        setCart(data?.adjustCartItemVariantSubscription || null);
      } else {
        // Use regular mutation for non-subscription items
        const { data } = await adjustCartItemVariantMutation({
          variables: {
            cartId: cartId,
            input: {
              variantGtin,
              quantity,
            },
          },
        });
        setCart(data?.adjustCartItemVariant || null);
      }
    } catch (e) {
      // logged in errorLink
      console.error("Error updating item in cart:", e);
    } finally {
      setLoading(false);
    }
  };

  // refresh cart on currentUserId and tab changes
  useEffect(() => {
    refreshCart();
    /* Event listener to refresh cart after changing tabs */
    /* TODO: debounce? */
    const refreshIfTabHasChanged = () => {
      if (document?.visibilityState === "visible") refreshCart();
    };
    window?.addEventListener("visibilitychange", refreshIfTabHasChanged);
    return function removeFocusRefresh() {
      window?.removeEventListener("visibilitychange", refreshIfTabHasChanged);
    };
  }, [currentUserId, refreshCart]);

  // update cookies depending on cart id changes
  useEffect(() => {
    if (!loading) {
      if (cart?.id) {
        if (cart.id !== getCartIdFromCookie()) {
          saveCartCookie(cart.id);
        }
      } else {
        deleteCartCookie();
      }
    }
  }, [cart?.id, loading]);

  const validationErrors = useMemo<V3ValidationErrorFragment[]>(() => {
    return (cart?.validationErrors?.filter(booleanFilter) ||
      []) as V3ValidationErrorFragment[];
  }, [cart?.validationErrors]);

  // TODO: consider using recoil.js when it is stable
  const cartContextValue: CartContextInterface = {
    cart,
    loading,
    validationErrors,
    refreshCart,
    addCartItem: addToCart,
    updateCartItem: updateCart,
    getSize: () => {
      return loading
        ? undefined
        : cart?.variantItems
            ?.filter((item): item is NonNullable<typeof item> => item != null)
            ?.map(({ quantity }) => quantity)
            .reduce((a, b) => a + b, 0) || 0;
    },
    getCartId,
  };

  return { cartContextValue };
}
